import React, { useState, useCallback, useMemo, useContext, useEffect } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb/BreadCrumb";
import TableContainerMin from "../../../Components/Common/TableContainerMin";
import { useQuery, useQueryClient } from "react-query";
import { CheckOutlined, DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { Button, Modal, Spin, Tag } from "antd";
import HistoryContext from "../../../Components/Hooks/HistoryContext";
import toast from "react-hot-toast";
import "../../Styles/style.css";
import { getInspections, valideInspection } from "../services/inspectionApi";
import DetailInspection from "./DetailInspection";
import { getUserById } from "../../Users/services/userApi";
import DataExport from "../../Exportcsv/DataExport";
import { getDateRangeTitle } from "../../../Components/Common/Date/triDate";

const Incidents = () => {
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();
  useEffect(() => {
    document.title = `Incidents | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;
  }, []);

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [selectedInspectionData, setSelectedInspectionData] = useState(null);

  const { isLoading, isError, error, data: inspectData } = useQuery("incidents", getInspections);
  const incidents = inspectData?.filter(inspection => inspection.category === "Incident");
  
  const toggleDetailModal = useCallback((inspection = null) => {
    setSelectedInspection(inspection);
    setIsDetailModalOpen(prev => !prev);
  });

  const handleDetail = (inspection) => {
    toggleDetailModal(inspection);
  };

  const handleConfirm = async () => {
    if (!selectedInspectionData) return;
    try {
      await valideInspection(selectedInspectionData.id, "Résolues");
      queryClient.invalidateQueries("incidents");
      toast.success("L'incident a été validée avec succès 🎉");
    } catch (error) {
      console.error("Error validation inspection:", error);
      toast.error(`😔 Erreur lors de la validation de l'incident : ${error?.message || error}`);
    } finally {
      setConfirmModal(false);
    }
  };

  const openConfirmModal = (inspection) => {
    setSelectedInspectionData(inspection);
    setConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setConfirmModal(false);
  };

  const columns = useMemo(() => [
    { Header: "Numéro", accessor: "id", filterable: false },
    { Header: "Type d'inspection", accessor: "typeInspection", filterable: false },
    {
      Header: "Chauffeur",
      accessor: "userName",
      Cell: ({ row }) => (
        <div>{row.original.userName || "-"}</div>
      ),
      filterable: false,
    },
    {
      Header: "Véhicule",
      accessor: row => row.vehicle?.brand,
      filterable: false
    },
    {
      Header: "Date",
      accessor: "createdAt",
      Cell: ({ value }) => 
        value ? new Date(value).toLocaleDateString('fr-FR') : "-",
      filterable: false,
    },
    {
      Header: "Statut",
      accessor: "status",
      Cell: ({ value }) => (
        <span className={`badge bg-${value === "Résolues" ? "success" : "danger"}-subtle text-${value === "Résolues" ? "success" : "danger"}`}>
          {value}
        </span>
      ),
      filterable: false,
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button
            title="Détail"
            size="sm"
            onClick={() => handleDetail(row.original)}
            className="btn btn-soft-info"
            aria-label="Voir les détails de l'inspection"
          >
            <EyeOutlined />
          </Button>
          <Button
            title="Résoudre"
            size="sm"
            onClick={() => openConfirmModal(row.original)}
            className="btn btn-soft-success"
            aria-label="Valider l'inspection"
          >
            <CheckOutlined />
          </Button>
        </div>
      ),
      filterable: false,
    },
  ], [openConfirmModal, handleDetail, selectedInspectionData]);

  const tabs = ['Toutes', 'Incident', 'Intérieure', 'Extérieure', 'Fonctionnelle'];
  const [activeTab, setActiveTab] = useState('Toutes');

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const HeaderTable = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex gap-3 mb-3">
        {tabs.map((tab) => (
          <Button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={`btn ${activeTab === tab ? 'active-tab' : 'btn-light'}`}
          >
            {tab}
          </Button>
        ))}
      </div>
    </div>
  );

//   const filteredIncidents = useMemo(() => {
//     if (!inspections) return [];
//     switch (activeTab) {
//       case 'Intérieure':
//         return inspections.filter(v => v.category === 'Interior' || v.category === 'interior');
//       case 'Extérieure':
//         return inspections.filter(v => v.category === 'Exterior');
//       case 'Fonctionnelle':
//         return inspections.filter(v => v.category === 'Functional');
//       case 'Incident':
//         return inspections.filter(v => v.category === 'Incident');
//       default:
//         return inspections;
//     }
//   }, [inspections, activeTab]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page="Incidents" history={history} />
          <Row>
            <Col>
              <Card id="contactList">
                <CardHeader>
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="text-title mb-4">
                      Incidents{" "}
                      <Tag color="#5ea8adff" style={{ fontSize: "12px" }}>
                        {incidents?.length || 0}
                      </Tag>
                    </div>

                    <DataExport
                      title={getDateRangeTitle(incidents, "createdAt", "Liste des incidents du")}
                      fileName="Incidents_Export.xlsx"
                      columns={columns}
                      data={incidents}
                      buttonLabel="Exporter"
                      buttonType="primary"
                      buttonClass="active-tab"
                      icon={<DownloadOutlined />}
                    />

                  </div>
                </CardHeader>
                <CardBody className="pt-0">
                  <TableContainerMin
                    columns={columns}
                    data={incidents || []}
                    isGlobalSearch={true}
                    isGlobalFilter={true}
                    customPageSize={5}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light"
                    SearchPlaceholder="Recherche..."
                  />
                  {isLoading && !isError && (
                    <div className="text-center">
                      Chargement des inspections 😉, Merci de patienter ... <Spin />
                    </div>
                  )}
                  {incidents?.length === 0 && !isLoading && !isError && (
                    <div className="text-center">Aucune inspection {activeTab} à afficher pour le moment 😌.</div>
                  )}
                  {isError && !isLoading && (
                    <div className="text-center">Erreur lors du chargement des inspections : {error.message}</div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          <DetailInspection
            modal={isDetailModalOpen}
            setModal={setIsDetailModalOpen}
            toggle={toggleDetailModal}
            selectedInspection={selectedInspection}
          />
          <Modal
            title={`Résolution de l'inspection : ${selectedInspectionData?.typeInspection}`}
            open={confirmModal}
            onCancel={closeConfirmModal}
            footer={[
              <Button key="cancel" onClick={closeConfirmModal} className="btn btn-soft-primary">
                Annuler
              </Button>,
              <Button key="valide" type="danger" className="active-tab" onClick={handleConfirm}>
                Oui, Valider
              </Button>,
            ]}
          >
            <p className="text-center">Considerer l'inspection <Tag color="cyan"> {selectedInspectionData?.typeInspection}</Tag> comme étant résolue ?</p>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Incidents;
